import { quotesAPI } from 'api/quotesAPI'
import { mergeObjects } from 'components/Common/ObjectManagement/ObjectManagement'
import { useState } from 'react'

interface Summary {
  applyAutomaticAllocation: boolean
  applyDestinationLogic: boolean
}

const DEFAULT_QUOTE_ERROR_MESSAGE = 'There was an error getting your quote.'

const parseQuoteError = (source: any) => {
  const responseStatus = source?.response?.status ?? source?.status
  const responseData = source?.response?.data ?? source?.data
  const apiError = responseData?.error || source?.data?.error || source?.error
  const validationErrors = Array.isArray(apiError?.validationErrors)
    ? apiError.validationErrors
    : []
  const firstValidationError = validationErrors?.[0]

  const validationMessage = firstValidationError?.message

  return {
    status: responseStatus,
    code: apiError?.code,
    reason: apiError?.reason,
    message:
      validationMessage ||
      apiError?.message ||
      source?.message ||
      DEFAULT_QUOTE_ERROR_MESSAGE,
    summaryMessage: apiError?.message,
    validationErrors,
  }
}

export default function useQuote() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [errorDetails, setErrorDetails] = useState<any>(null)
  const [prices, setPrices] = useState<any>()
  const [reference, setReference] = useState<any>()
  const [summary, setSummary] = useState<Summary>()
  const [inboundReference, setInboundReference] = useState<any>()

  const createQuote = async (data: object) => {
    setIsLoading(true)
    setError(false)
    setErrorDetails(null)
    try {
      const resp = await quotesAPI.createQuote(data)
      if (resp && resp.status !== 204) {
        setSummary(resp.data?.summary)
        setReference(resp.data.outbound.quoteReference)
        setInboundReference(resp.data.inbound?.quoteReference)
        const vehiclePrices = resp.data.outbound.vehicles
          .map((vehicle: any) => {
            let price = vehicle.estimatedTotal
            let inboundCar
            if (resp.data.inbound?.vehicles?.length > 0) {
              inboundCar = resp.data.inbound.vehicles.find(
                (inboundVehicle: any) =>
                  inboundVehicle.vehicleTypeName === vehicle.vehicleTypeName,
              )
              if (inboundCar && !resp.data?.summary?.applyAutomaticAllocation) {
                const currentPrice = price
                price = mergeObjects(currentPrice, inboundCar.estimatedTotal)
                price.currency = currentPrice.currency
              }
            }

            let avilableReturnVehicle = true
            if (inboundCar === undefined && data.journeyTypeName === 'Return') {
              avilableReturnVehicle = false
            }

            let containsDestinationSupplier = vehicle.isDestinationLogicQuote
            if (
              (inboundCar != undefined && inboundCar.isDestinationLogicQuote) ||
              vehicle.isDestinationLogicQuote === 'true'
            ) {
              containsDestinationSupplier = true
            }
            return {
              supplierQuoteReference: vehicle.supplierQuoteReference,
              inboundSupplierQuoteReference: inboundCar?.supplierQuoteReference,
              vehicleTypeName: vehicle.vehicleDetails.vehicleTypeName,
              price,
              inboundPrice: inboundCar.estimatedTotal,
              estimatedPriceCurrency: vehicle.estimatedTotal.currency,
              eta: vehicle.estimatedArrivalTime,
              isDestinationLogicQuote: containsDestinationSupplier,
              isAvilableReturnVehicle: avilableReturnVehicle,
              vehicleDetails: vehicle.vehicleDetails,
              alternativeOptions: vehicle?.alternativeOptions || null,
              inboundAlternativeOptions: inboundCar?.alternativeOptions || null,
              isTrackable: vehicle?.vehicleDetails?.isTrackable,
            }
          })
          .filter((vehicle: any) => vehicle.isAvilableReturnVehicle === true)
          .sort((a: any, b: any) => (a.price.price > b.price.price ? 1 : -1))

        setPrices(vehiclePrices)
      }

      if (resp && resp.data?.error) {
        setError(true)
        setErrorDetails(parseQuoteError(resp))
      }
    } catch (error: any) {
      setError(true)
      setErrorDetails(parseQuoteError(error))
    }

    setIsLoading(false)
  }

  const createTypeOrderedQuote = async (data: object) => {
    setIsLoading(true)
    setError(false)
    setErrorDetails(null)
    try {
      const resp = await quotesAPI.createQuote(data)
      if (resp && resp.status !== 204) {
        if (resp.data?.error || !resp.data?.outbound) {
          setError(true)
          setErrorDetails(parseQuoteError(resp))
          setIsLoading(false)
          return
        }
        setSummary(resp.data?.summary)
        setReference(resp.data.outbound.quoteReference)
        setInboundReference(resp.data.inbound?.quoteReference)
        const vehiclePrices = resp.data.outbound.vehicles
          .map((vehicle: any) => {
            let price = vehicle.estimatedTotal

            let inboundCar
            if (resp.data.inbound?.vehicles?.length > 0) {
              inboundCar = resp.data.inbound.vehicles.find(
                (inboundVehicle: any) =>
                  inboundVehicle.vehicleDetails?.vehicleTypeName ===
                  vehicle?.vehicleDetails.vehicleTypeName,
              )
              if (inboundCar && !resp.data?.summary?.applyAutomaticAllocation) {
                const currentPrice = price
                price = mergeObjects(currentPrice, inboundCar.estimatedTotal)
                price.currency = currentPrice.currency
              }
            }

            let avilableReturnVehicle = true
            if (inboundCar === undefined && data.journeyTypeName === 'Return') {
              avilableReturnVehicle = false
            }

            let containsDestinationSupplier = vehicle.isDestinationLogicQuote
            if (
              (inboundCar != undefined && inboundCar.isDestinationLogicQuote) ||
              vehicle.isDestinationLogicQuote === 'true'
            ) {
              containsDestinationSupplier = true
            }

            if (vehicle?.alternativeOptions?.length) {
              vehicle.alternativeOptions = vehicle?.alternativeOptions?.map(
                (v: any) => ({
                  ...v,
                  price: parseFloat(
                    v?.option?.estimatedTotal?.price?.toFixed(2),
                  ),
                }),
              )
            }
            if (inboundCar?.alternativeOptions?.length) {
              inboundCar.alternativeOptions =
                inboundCar?.alternativeOptions?.map((v: any) => ({
                  ...v,
                  price: parseFloat(
                    v?.option?.estimatedTotal?.price?.toFixed(2),
                  ),
                }))
            }

            return {
              supplierQuoteReference: vehicle.supplierQuoteReference,
              isPreferredSupplier: vehicle.isPreferredSupplier,
              isOffline: vehicle.isOffline,
              inboundSupplierQuoteReference: inboundCar?.supplierQuoteReference,
              vehicleTypeName: vehicle.vehicleDetails.vehicleTypeName,
              price,
              inboundPrice: inboundCar?.estimatedTotal,
              estimatedPriceCurrency: vehicle.estimatedTotal.currency,
              eta: vehicle.estimatedArrivalTime,
              supplierName: vehicle?.supplierName,
              supplierAlias: vehicle?.supplierAlias,
              supplierContactNumber: vehicle?.supplierContactNumber,
              supplierLogo: vehicle?.supplierLogo,
              isDestinationLogicQuote: containsDestinationSupplier,
              isAvilableReturnVehicle: avilableReturnVehicle,
              vehicleDetails: vehicle.vehicleDetails,
              cancellationPolicy:
                vehicle?.vehicleDetails?.cancellationPolicy ??
                vehicle?.cancellationPolicy,
              inboundCancellationPolicy:
                inboundCar?.vehicleDetails?.cancellationPolicy ??
                inboundCar?.cancellationPolicy,
              alternativeOptions: vehicle?.alternativeOptions || null,
              inboundAlternativeOptions: inboundCar?.alternativeOptions || null,
              isTrackable: vehicle?.vehicleDetails?.isTrackable,
            }
          })
          .filter((vehicle: any) => vehicle.isAvilableReturnVehicle === true)
          .sort((a: any, b: any) => (a.price.price > b.price.price ? 1 : -1))
        setPrices(
          vehiclePrices.reduce((rv: any, x: any) => {
            const array = (rv[x['vehicleTypeName']] =
              rv[x['vehicleTypeName']] || [])
            array.push(x)
            return rv
          }, {}),
        )
      }

      if (resp && resp.data?.error) {
        setError(true)
        setErrorDetails(parseQuoteError(resp))
      }
    } catch (error: any) {
      console.log({ error })
      setError(true)
      setErrorDetails(parseQuoteError(error))
    }

    setIsLoading(false)
  }

  const createReallocateQuote = async (
    id: any,
    currentCarType: string,
    currentSupplierName: string,
  ) => {
    setIsLoading(true)
    setError(false)
    setErrorDetails(null)
    try {
      const resp = await quotesAPI.createReallocationQuote(id)
      if (resp && resp.status !== 204) {
        setSummary(resp.data?.summary)
        setReference(resp.data.outbound.quoteReference)
        const vehiclePrices = resp.data.outbound.vehicles.map(
          (vehicle: any) => {
            let price = parseFloat(vehicle.estimatedTotal.price?.toFixed(2))

            let inboundCar
            if (resp.data.inbound?.vehicles?.length > 0) {
              inboundCar = resp.data.inbound.vehicles.find(
                (inboundVehicle: any) =>
                  inboundVehicle.vehicleTypeName === vehicle.vehicleTypeName,
              )
              if (inboundCar) {
                const currentPrice = parseFloat(price.toFixed(2))
                price =
                  currentPrice +
                  parseFloat(inboundCar.estimatedTotal?.price?.toFixed(2))
              }
            }

            return {
              supplierQuoteReference: vehicle.supplierQuoteReference,
              inboundSupplierQuoteReference: inboundCar?.supplierQuoteReference,
              vehicleTypeName: vehicle.vehicleDetails.vehicleTypeName,
              supplierName: vehicle.supplierName,
              supplierAlias: vehicle.supplierAlias,
              supplierContactNumber: vehicle.supplierContactNumber,
              price,
              eta: vehicle.estimatedArrivalTime,
              isDestinationLogicQuote: vehicle.isDestinationLogicQuote,
              vehicleDetails: vehicle.vehicleDetails,
            }
          },
        )
        vehiclePrices.sort((a: any, b: any) => (a.price > b.price ? 1 : -1))
        const carTypeSupplierKey = currentSupplierName.concat(currentCarType)
        setPrices(
          vehiclePrices
            .filter(
              (quote: any) =>
                quote.supplierName.concat(
                  quote.vehicleDetails.vehicleTypeName,
                ) !== carTypeSupplierKey,
            )
            .reduce((rv: any, x: any) => {
              const array = (rv[x['vehicleTypeName']] =
                rv[x['vehicleTypeName']] || [])
              array.push(x)
              return rv
            }, {}),
        )
      }

      if (resp && resp.data?.error) {
        setError(true)
        setErrorDetails(parseQuoteError(resp))
      }
    } catch (error: any) {
      setError(true)
      setErrorDetails(parseQuoteError(error))
    }

    setIsLoading(false)
  }

  return {
    prices,
    summary,
    reference,
    inboundReference,
    createQuote,
    createTypeOrderedQuote,
    createReallocateQuote,
    isLoading,
    error,
    errorDetails,
  }
}
